import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, rupiah } from "@/lib/api";
import { Empty, NativeSelect, Spinner } from "@/components/Brand";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";

const ST = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };
const STL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak" };

const ProofImage = ({ path, id }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(""));
    return () => url && URL.revokeObjectURL(url);
  }, [path]);
  if (src === null) return <span className="text-xs text-slate-400">Memuat...</span>;
  if (!src) return <span className="text-xs text-red-600">Gagal memuat</span>;
  return <a href={src} target="_blank" rel="noreferrer" data-testid={`payment-proof-${id}`}><img src={src} alt="Bukti" className="h-16 w-16 rounded border object-cover" /></a>;
};

const Row = ({ p, onDone }) => {
  const [note, setNote] = useState(p.admin_note || "");
  const review = async (status) => {
    try {
      await api.put(`/admin/payments/${p.id}`, { status, admin_note: note });
      toast.success(status === "approved" ? "Pembayaran disetujui, akses paket terbuka" : "Pembayaran ditolak");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <tr className="border-t align-top" data-testid={`payment-row-${p.id}`}>
      <td className="px-4 py-3 font-semibold">{p.user_name}<div className="text-xs font-normal text-slate-500">{p.user_email}</div></td>
      <td className="py-3">{p.package_name}<div className="text-xs text-slate-500">{rupiah(p.amount)}</div></td>
      <td className="py-3 text-xs">{fmtDate(p.created_at)}<div className="text-slate-500">{p.note}</div></td>
      <td className="py-3">{p.proof_path ? <ProofImage path={p.proof_path} id={p.id} /> : <span className="text-xs font-semibold text-blue-700" data-testid={`payment-midtrans-${p.id}`}>Midtrans<br /><span className="font-mono font-normal text-slate-500">{p.order_id}<br />{p.gateway_status}</span></span>}</td>
      <td className="py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${ST[p.status]}`}>{STL[p.status]}</span></td>
      <td className="space-y-2 py-3 pr-4">
        {p.method === "midtrans" && ["settlement", "capture"].includes(p.gateway_status) ? <span className="text-xs font-semibold text-emerald-700">Lunas via Midtrans</span> : <>
        <Input placeholder="Catatan admin" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 text-xs" data-testid={`payment-note-${p.id}`} />
        <div className="flex gap-2">
          <button onClick={() => review("approved")} className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white" data-testid={`payment-approve-${p.id}`}>Setujui</button>
          <button onClick={() => review("rejected")} className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white" data-testid={`payment-reject-${p.id}`}>Tolak</button>
        </div></>}
      </td>
    </tr>
  );
};

export default function PaymentsAdmin() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/payments", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div>
      <PageTitle title="Pembayaran" desc="Verifikasi transfer manual. Pembayaran online Midtrans disetujui otomatis setelah lunas. Akses paket berbayar terbuka otomatis setelah pembayaran disetujui." />
      <div className="mb-4 max-w-xs"><NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="payments-status-filter"><option value="pending">Menunggu verifikasi</option><option value="approved">Disetujui</option><option value="rejected">Ditolak</option><option value="">Semua</option></NativeSelect></div>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty text="Tidak ada pembayaran." /> : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full min-w-[860px] text-sm" data-testid="payments-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Peserta</th><th>Paket</th><th>Tanggal</th><th>Bukti</th><th>Status</th><th>Tindakan</th></tr></thead>
            <tbody>{rows.map((p) => <Row key={p.id} p={p} onDone={load} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
